import React from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CategoriaTorneio } from '../../../services/categoriaTorneioService';
import { formatCurrency } from '../../../utils/money';
import {
  formatModeloDisputa,
  formatTipoPontuacao,
  formatGenero,
  formatTipoCategoria,
  formatNumeroSets,
  formatConfigFilaEspera,
  formatBooleanSimNao,
  formatDataCategoria,
} from '../../../utils/categoriaTorneioLabels';
import { temFaseDeGrupos } from '../../../utils/temFaseDeGrupos';
import CategoriaStatusChip from '../../../components/CategoriaStatusChip';

interface CategoryConfigTabProps {
  categoriaId: string;
  categoria: CategoriaTorneio;
  torneioId: string;
  navigate: NavigateFunction;
}

const NAO_DEFINIDO = 'Não definido';

/** Item label/valor do resumo (somente leitura). */
const ConfigItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="col-md-4 mb-3">
    <strong>{label}:</strong>
    <p className="text-muted mb-0">{value}</p>
  </div>
);

/** Seção de campos do resumo, com título. */
const ConfigSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <>
    <h5 className="fw-700 mt-4 mb-3">{title}</h5>
    <div className="row">{children}</div>
  </>
);

/**
 * Componente da aba "Configuração" do gerenciamento de categoria.
 *
 * Exibe um resumo somente informativo das configurações atuais da categoria.
 * Para editá-las, o usuário deve acionar "Editar Categoria", que leva à tela
 * de edição (este componente não altera dados).
 */
const CategoryConfigTab: React.FC<CategoryConfigTabProps> = ({
  categoriaId,
  categoria,
  torneioId,
  navigate,
}) => {
  const validarElegibilidade = categoria.torneio?.validarElegibilidade ?? false;
  const isDupla = categoria.numeroAtletas === 2;
  const isGruposEliminacao = temFaseDeGrupos(categoria.modeloDisputa);
  // Rodízio (Rei/Rainha, Super 8): a config relevante é o tamanho do grupo;
  // quantidadeGrupos é derivado e classificadosPorGrupo é 0 (não exibidos).
  const isRodizio =
    categoria.modeloDisputa === 'REI_RAINHA' || categoria.modeloDisputa === 'SUPER_8';

  const vagasLabel = categoria.vagas != null ? String(categoria.vagas) : NAO_DEFINIDO;
  const gamesPorSetsLabel =
    categoria.gamesPorSets != null ? `${categoria.gamesPorSets} games` : NAO_DEFINIDO;
  const tiebreaksLabel = categoria.tiebreaks != null ? String(categoria.tiebreaks) : NAO_DEFINIDO;

  return (
    <div className="mt-4">
      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
            <div>
              <h4 className="fw-700 mb-1">Configurações da Categoria</h4>
              <p className="text-muted mb-0">
                Resumo das configurações atuais desta categoria. Para alterá-las, clique em
                &ldquo;Editar Categoria&rdquo;.
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                navigate(`/liga/tournament/${torneioId}/category/${categoriaId}`);
              }}
            >
              Editar Categoria
            </button>
          </div>

          <ConfigSection title="Dados básicos">
            <ConfigItem label="Nome" value={categoria.nome || NAO_DEFINIDO} />
            <ConfigItem label="Tipo" value={formatTipoCategoria(categoria.numeroAtletas)} />
            <ConfigItem
              label="Status"
              value={
                <CategoriaStatusChip
                  status={categoria.status}
                  temPartidas={(categoria.fasesCategoria || []).flatMap((f) => f.partidas || []).filter((p) => p.status !== 'CANCELADA').length > 0}
                />
              }
            />
            <ConfigItem label="Vagas" value={vagasLabel} />
            <ConfigItem label="Preço da inscrição" value={formatCurrency(categoria.precoInscricao)} />
            <ConfigItem
              label="Aplicar descontos do torneio"
              value={formatBooleanSimNao(categoria.aplicarDescontoTorneio)}
            />
          </ConfigSection>

          <ConfigSection title="Disputa">
            <ConfigItem label="Modelo de disputa" value={formatModeloDisputa(categoria.modeloDisputa)} />
            {isRodizio ? (
              <ConfigItem
                label="Tamanho de grupo"
                value={categoria.tamanhoGrupo != null ? String(categoria.tamanhoGrupo) : NAO_DEFINIDO}
              />
            ) : isGruposEliminacao ? (
              <>
                <ConfigItem
                  label="Quantidade de grupos"
                  value={categoria.quantidadeGrupos != null ? String(categoria.quantidadeGrupos) : NAO_DEFINIDO}
                />
                <ConfigItem
                  label="Classificados por grupo"
                  value={
                    categoria.classificadosPorGrupo != null
                      ? String(categoria.classificadosPorGrupo)
                      : NAO_DEFINIDO
                  }
                />
              </>
            ) : null}
          </ConfigSection>

          <ConfigSection title="Placar">
            <ConfigItem label="Tipo de pontuação" value={formatTipoPontuacao(categoria.tipoPontuacao)} />
            <ConfigItem label="Número de sets" value={formatNumeroSets(categoria.numeroSets)} />
            <ConfigItem label="Games por set" value={gamesPorSetsLabel} />
            <ConfigItem label="Tiebreak" value={tiebreaksLabel} />
          </ConfigSection>

          <ConfigSection title="Datas">
            <ConfigItem label="Início" value={formatDataCategoria(categoria.dataInicio)} />
            <ConfigItem label="Fim" value={formatDataCategoria(categoria.dataFim)} />
            <ConfigItem label="Início das inscrições" value={formatDataCategoria(categoria.torneio.dataInscricaoInicio)} />
            <ConfigItem label="Fim das inscrições" value={formatDataCategoria(categoria.torneio.dataInscricaoFim)} />
            <ConfigItem label="Limite de pagamento" value={formatDataCategoria(categoria.dataLimPag)} />
          </ConfigSection>

          {validarElegibilidade && (
            <ConfigSection title="Elegibilidade">
              <ConfigItem label="Gênero" value={formatGenero(categoria.genero)} />
              <ConfigItem
                label="Idade inicial"
                value={categoria.idadeInicial != null ? String(categoria.idadeInicial) : NAO_DEFINIDO}
              />
              <ConfigItem
                label="Idade final"
                value={categoria.idadeFinal != null ? String(categoria.idadeFinal) : NAO_DEFINIDO}
              />
              {isDupla && (
                <ConfigItem
                  label="Idade combinada"
                  value={categoria.idadeCombinada != null ? String(categoria.idadeCombinada) : NAO_DEFINIDO}
                />
              )}
            </ConfigSection>
          )}

          <ConfigSection title="Inscrições">
            <ConfigItem label="Fila de espera" value={formatConfigFilaEspera(categoria.configFilaEspera)} />
          </ConfigSection>
        </div>
      </div>
    </div>
  );
};

export default CategoryConfigTab;
